// Leistungskatalog für österreichische Arbeitsmedizin
// Preise als EuroCents (Integer), nie Float

import { type EuroCents, euroToEuroCents } from '../../../types/branded';

export type LeistungsTyp = 'VGÜ' | 'Beratung' | 'Begehung' | 'Gutachten' | 'Schulung' | 'Sonstige';

export interface Leistung {
  readonly code:       string;
  readonly bezeichnung: string;
  readonly typ:        LeistungsTyp;
  readonly einheit:    string;       // 'Std', 'Stück', 'Pauschale'
  readonly preisNetto: EuroCents;   // Standardpreis netto
  readonly ustSatz:    0 | 10 | 20; // Österreich: VGÜ = 0% (unecht steuerfrei), Beratung = 20%
  readonly beschreibung?: string | undefined;
}

// Leistungskatalog — Standardpreise (individuell überschreibbar via Honorarvereinbarung)
export const LEISTUNGEN: readonly Leistung[] = [
  // ── VGÜ ──────────────────────────────────────────────────────────────────
  {
    code: 'VGÜ-01',
    bezeichnung: 'VGÜ Einstellungsuntersuchung',
    typ: 'VGÜ',
    einheit: 'Stück',
    preisNetto: euroToEuroCents(85),
    ustSatz: 0,
    beschreibung: 'Vollständige Eignungsuntersuchung inkl. Labor-Basisprofil',
  },
  {
    code: 'VGÜ-02',
    bezeichnung: 'VGÜ Folgeuntersuchung allgemein',
    typ: 'VGÜ',
    einheit: 'Stück',
    preisNetto: euroToEuroCents(65),
    ustSatz: 0,
    beschreibung: 'Allgemeine VGÜ ohne spezifische Gefährdung',
  },
  {
    code: 'VGÜ-03',
    bezeichnung: 'VGÜ Lärm',
    typ: 'VGÜ',
    einheit: 'Stück',
    preisNetto: euroToEuroCents(75),
    ustSatz: 0,
    beschreibung: 'VGÜ mit Audiometrie gemäß § 5 Abs. 1 Z 1-2 VGÜ-VO',
  },
  {
    code: 'VGÜ-04',
    bezeichnung: 'VGÜ Gefährdung Chemie/Staub',
    typ: 'VGÜ',
    einheit: 'Stück',
    preisNetto: euroToEuroCents(95),
    ustSatz: 0,
    beschreibung: 'VGÜ mit erweitertem Labor und Lungenfunktion',
  },
  {
    code: 'VGÜ-05',
    bezeichnung: 'VGÜ Bildschirmarbeit',
    typ: 'VGÜ',
    einheit: 'Stück',
    preisNetto: euroToEuroCents(45),
    ustSatz: 0,
    beschreibung: 'Vereinfachte VGÜ gemäß BSVA (Sehtest, WS-Screening)',
  },
  {
    code: 'VGÜ-06',
    bezeichnung: 'VGÜ Nacht-/Schichtarbeit',
    typ: 'VGÜ',
    einheit: 'Stück',
    preisNetto: euroToEuroCents(75),
    ustSatz: 0,
  },
  {
    code: 'VGÜ-07',
    bezeichnung: 'VGÜ ionisierende Strahlung',
    typ: 'VGÜ',
    einheit: 'Stück',
    preisNetto: euroToEuroCents(105),
    ustSatz: 0,
    beschreibung: 'VGÜ Kat. A/B inkl. Blutbild, Diff-BB, Augen',
  },
  // ── Beratung ──────────────────────────────────────────────────────────────
  {
    code: 'BER-01',
    bezeichnung: 'Arbeitsmedizinische Beratung',
    typ: 'Beratung',
    einheit: 'Std',
    preisNetto: euroToEuroCents(120),
    ustSatz: 20,
    beschreibung: 'Beratungsgespräch/Telefonat je angefangene Stunde',
  },
  {
    code: 'BER-02',
    bezeichnung: 'Erste Hilfe Ausbildung (Auffrischung)',
    typ: 'Schulung',
    einheit: 'Pauschale',
    preisNetto: euroToEuroCents(350),
    ustSatz: 20,
    beschreibung: 'Halbtagsschulung bis 12 Personen',
  },
  {
    code: 'BER-03',
    bezeichnung: 'Unterweisung Arbeitnehmer/innen',
    typ: 'Schulung',
    einheit: 'Std',
    preisNetto: euroToEuroCents(100),
    ustSatz: 20,
  },
  // ── Begehung ──────────────────────────────────────────────────────────────
  {
    code: 'BEG-01',
    bezeichnung: 'Betriebsbegehung',
    typ: 'Begehung',
    einheit: 'Std',
    preisNetto: euroToEuroCents(130),
    ustSatz: 20,
    beschreibung: 'Arbeitsplatzbegehung inkl. Dokumentation',
  },
  {
    code: 'BEG-02',
    bezeichnung: 'ASA-Sitzung',
    typ: 'Begehung',
    einheit: 'Std',
    preisNetto: euroToEuroCents(130),
    ustSatz: 20,
    beschreibung: 'Arbeitssicherheitsausschuss-Sitzung',
  },
  {
    code: 'BEG-03',
    bezeichnung: 'Gefährdungsbeurteilung (GBU)',
    typ: 'Begehung',
    einheit: 'Std',
    preisNetto: euroToEuroCents(130),
    ustSatz: 20,
  },
  // ── Gutachten ──────────────────────────────────────────────────────────────
  {
    code: 'GUT-01',
    bezeichnung: 'Ärztliches Attest / Kurzgutachten',
    typ: 'Gutachten',
    einheit: 'Stück',
    preisNetto: euroToEuroCents(60),
    ustSatz: 0,
  },
  {
    code: 'GUT-02',
    bezeichnung: 'Arbeitsmedizinisches Gutachten',
    typ: 'Gutachten',
    einheit: 'Std',
    preisNetto: euroToEuroCents(180),
    ustSatz: 0,
    beschreibung: 'Ausführliches Gutachten inkl. Aktenstudium',
  },
  // ── Fahrtkosten ──────────────────────────────────────────────────────────
  {
    code: 'FKT-01',
    bezeichnung: 'Fahrtkostenersatz (km)',
    typ: 'Sonstige',
    einheit: 'km',
    preisNetto: euroToEuroCents(0.50),
    ustSatz: 20,
    beschreibung: 'Amtlicher Kilometersatz',
  },
  {
    code: 'FKT-02',
    bezeichnung: 'Reisezeitvergütung',
    typ: 'Sonstige',
    einheit: 'Std',
    preisNetto: euroToEuroCents(65),
    ustSatz: 20,
  },
] as const;

export const LEISTUNG_MAP = new Map<string, Leistung>(
  LEISTUNGEN.map(l => [l.code, l])
);
